import { Provider, useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { initStore } from 'src/infra/stores/store';
import React, { PropsWithChildren, Suspense } from 'react';
import PageLoader from 'src/presentations/components/molecules/pageLoader';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { routesConfig } from 'src/main/router';
import { getReactQueryClient } from 'src/utils/reactQuery';
import Logger from 'js-logger';
import { Amplify } from 'aws-amplify';
import { awsConfig } from 'src/config';
import authSelectors from 'src/infra/stores/reducer/auth/selectors';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { UploadSelector } from 'src/presentations/components/molecules/uploadInvoice/UploadSelector';
import { ToastResultSelector } from 'src/presentations/components/molecules/toast/ToastResultSelector';
import { NoRateCardSelector } from 'src/presentations/components/organisms/actionDetails/noRateCard/Selector';
import { ContactSelector } from 'src/presentations/components/molecules/contact/ContactSelector';
import { SidebarProvider } from 'src/presentations/components/organisms/sidebar/SidebarContext';
import { FreyaPopoverProvider } from 'src/presentations/components/organisms/askFreya/FreyaPopoverContext';
import * as Sentry from '@sentry/react';
import PageError from 'src/containers/PageError';
// Removed: Old SSE implementation
// import { NotificationStatusServiceSSE } from 'src/infra/services/notificationStatusServiceSSE';
// import { SSEProvider } from 'src/contexts/SSEContext';
import { SSEInitializer } from 'src/infra/services/sse/SSEConnectionManager';
import { UserDataSynchronizer } from './UserDataSynchronizer';
import MuiTheme from './MuiTheme';
import Notification from '../utils/notification';

import { GlobalDownloadListener } from 'src/presentations/components/organisms/analytics/GlobalDownloadListener';

const ENABLE_REACT_QUERY_DEV_TOOLS = false;

Logger.setLevel(Logger.INFO);
// eslint-disable-next-line react-hooks/rules-of-hooks
Logger.useDefaults();

Amplify.configure(awsConfig);

const router = createBrowserRouter(routesConfig);

// Init react query.
const queryClient = getReactQueryClient();

const store = initStore();

// Removed: Old SSE implementation

function FallbackComponent() {
  return <div>An error has occurred</div>;
}

const App: React.FC = () => (
  // ToDo: fix this and make it look nice.
  // https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
  <Sentry.ErrorBoundary fallback={<FallbackComponent />}>
    <Authenticator.Provider>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          {ENABLE_REACT_QUERY_DEV_TOOLS && <ReactQueryDevtools initialIsOpen />}
          <MuiTheme>
            <ErrorBoundary fallback={<PageError />}>
              <SSEInitializer />
              <UserDataSynchronizer>
                <SidebarProvider>
                  <SidebarProvider>
                    <FreyaPopoverProvider>
                      <AppWrapper>
                        <NoRateCardSelector>
                          <UploadSelector>
                            <ContactSelector>
                              <ToastResultSelector>
                                <RouterProvider router={router} />
                                <Notification />
                                <GlobalDownloadListener />
                              </ToastResultSelector>
                            </ContactSelector>
                          </UploadSelector>
                        </NoRateCardSelector>
                      </AppWrapper>
                    </FreyaPopoverProvider>
                  </SidebarProvider>
                </SidebarProvider>
              </UserDataSynchronizer>
            </ErrorBoundary>
          </MuiTheme>
        </QueryClientProvider>
      </Provider>
    </Authenticator.Provider>
  </Sentry.ErrorBoundary>
);

// Triggers auth flow
// Returns page loader while lazy routes load
// Wrapper is separated for reuse in tests
export const AppWrapper: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  // Show spinner while checking auth state
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const isLoadingUser = useSelector(authSelectors.selectIsLoading);

  if (authStatus === 'configuring' || isLoadingUser) {
    return <PageLoader />;
  }

  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
};

export default App;
